import React, { useMemo, useState, useRef, useEffect } from 'react';
import { askAndClockOut } from './clockOut';
import { Link, useNavigate } from 'react-router';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';
import { useDepartments } from '@battleops/client/src/context/DepartmentContext';
import { formatDuration, formatDurationShort, computeTotalTaskTime, isProjectLive } from '@battleops/shared';
import Icon from '../components/Icon';
import { useNow, useTaskModal } from '../hooks';
import UpdatePrompt from './UpdatePrompt';
import s from './shell.module.css';
import { HourChip } from '../components/Profit';
import { useProjectProfit } from '@battleops/client/src/data/useProfit';
import { useSession } from '@battleops/client/src/context/SessionContext';

// Same as the phone's Home: over 12 h on the clock is almost always a missed clock-out.
const STALE_MS = 12 * 3600000;

// The app bar, across the whole frame: the brand over the drawer, then one console — the clock row on top (where
// you're clocked in, the session, the task on the timer, break / clock out), a hairline, and under it the search
// (it opens the command bar right where it sits) with the update prompt and Add. The job's colour runs down the
// console's left edge so the two rows read as one instrument.
export default function TopBar({ onCommand, onAdd }) {
  return (
    <header className={s.top}>
      <Link to="/" className={s.brand} title="Home" aria-label="BattleOps — home">
        <span className={s.brandShield}><img src="/shield.png" alt="" /></span>
        <span className={s.word}>BATTLE<span>OPS</span></span>
      </Link>
      <ClockStrip>
        <div className={s.consoleRow}>
          <button className={s.searchBar} onClick={onCommand} title="Search ( / ) — from inside a note or a box: Ctrl Shift /">
            <Icon name="search" size={16} />
            <span className={s.searchText}>Search jobs, tasks, notes <span className={s.searchHint}>— / for commands</span></span>
            <span className="kbd">/</span>
          </button>
          <UpdatePrompt />
          <button className={`btn primary ${s.addBtn}`} onClick={onAdd} title="Quick add (N)"><Icon name="plus" size={15} />Add<span className="kbd">N</span></button>
        </div>
      </ClockStrip>
    </header>
  );
}

// The clock, desk-sized: where you're clocked in, the session time, the task on the timer and its time,
// break / resume and clock out. Off the clock: clock in to a project.
function ClockStrip({ children }) {
  const { clockedIn, tasks, timer, activeTaskId, pauseStatus, clockIn, clockOut, pauseTask, resumeTask, stopTravel, setTaskDone, completeTask } = useTasks();
  const { projects } = useProjects();
  const { deptForProject } = useDepartments();
  const navigate = useNavigate();
  const details = useTaskModal();
  const now = useNow(clockedIn ? 1000 : 30000);
  const [picking, setPicking] = useState(false);
  const profitFor = useProjectProfit();
  const { identity } = useSession() || {};

  if (!clockedIn) {
    return (
      <div className={s.console}>
        <div className={s.off} style={{ position: 'relative' }}>
          <span className={s.offDot} /><Icon name="clock" size={14} />Off the clock
          <button className="btn sm" onClick={() => setPicking(true)}><Icon name="play" size={13} />Clock in</button>
          {picking ? <ProjectPicker onPick={(pid) => { setPicking(false); clockIn(pid); }} onClose={() => setPicking(false)} /> : null}
        </div>
        {children}
      </div>
    );
  }
  const project = clockedIn.projectId ? projects.find((p) => p.id === clockedIn.projectId) : null;
  const dept = deptForProject(clockedIn.projectId);
  const task = activeTaskId ? tasks.find((t) => t.id === activeTaskId) : null;
  const taskMs = task ? (timer?.taskId === task.id ? computeTotalTaskTime(task, timer, now) : task.timeSpent || 0) : 0;
  const color = project?.color || dept?.color || 'var(--c-accent)';
  return (
    <div className={s.console} data-on style={{ '--console-c': color }}>
    <div className={s.strip}>
      <button className={s.stripWhere} onClick={() => project && navigate(`/projects/${project.id}`)} title="Open the project">
        <span className="dot" style={{ background: color }} /><span>{project?.icon} {project?.name || 'On the clock'}</span>
      </button>
      <span className={s.stripTime}>{formatDuration(now - clockedIn.startedAt)}</span>
      <HourChip project={project} me={identity?.uid} cost={profitFor(project)} />
      {now - clockedIn.startedAt > STALE_MS ? (
        <button className="chip" style={{ borderColor: 'var(--c-warning)', color: 'var(--c-warning)' }} onClick={() => navigate('/time/sessions')} title="On the clock over 12 hours">
          <Icon name="alert-triangle" size={12} />Forgot to clock out?
        </button>
      ) : null}
      {clockedIn.traveling ? (
        <button className="chip" style={{ borderColor: '#EC4899', color: '#EC4899' }} onClick={stopTravel}><Icon name="truck" size={12} />Travelling · stop</button>
      ) : null}
      <button className={s.stripTask} onClick={() => task && details.open(task.id)} disabled={!task} title={task ? 'Open the task' : ''}>
        <Icon name={pauseStatus ? 'coffee' : task ? 'play' : 'clock'} size={13} color={pauseStatus ? 'var(--c-warning)' : task ? 'var(--c-success)' : undefined} />
        <span>{pauseStatus ? `On ${pauseStatus.context || 'break'}` : task ? task.title : 'No task running'}</span>
        {task ? <span className="num faint">{formatDurationShort(taskMs)}</span> : null}
      </button>
      {task ? (
        <button className="btn sm" onClick={() => completeTask(task.id)} title="Finish the running task (its time is banked, the timer clears)"><Icon name="check" size={13} />Done</button>
      ) : null}
      {task ? (
        <button className="btn sm" onClick={() => (pauseStatus ? resumeTask() : pauseTask(task.id, 'break'))}>
          <Icon name={pauseStatus ? 'play' : 'coffee'} size={13} />{pauseStatus ? 'Resume' : 'Break'}
        </button>
      ) : null}
      <button className="btn sm danger" onClick={() => askAndClockOut({ tasks, activeTaskId, completeTask, clockOut })}><Icon name="stop-circle" size={13} />Clock out</button>
    </div>
    {children}
    </div>
  );
}

export function ProjectPicker({ onPick, onClose, allowNone = true }) {
  const { projects } = useProjects();
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const out = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    setTimeout(() => document.addEventListener('mousedown', out), 0);
    return () => document.removeEventListener('mousedown', out);
  }, [onClose]);
  const list = useMemo(() => {
    const act = projects.filter(isProjectLive);
    const f = q ? act.filter((p) => p.name.toLowerCase().includes(q.toLowerCase())) : act;
    return allowNone && !q ? [{ id: null, name: 'No project', icon: '⏱️' }, ...f] : f;
  }, [projects, q, allowNone]);
  return (
    <div className={s.pop} ref={ref} style={{ left: 0 }}>
      <input className="input" autoFocus placeholder="Clock in to…" value={q} onChange={(e) => { setQ(e.target.value); setI(0); }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setI((x) => Math.min(x + 1, list.length - 1)); }
          if (e.key === 'ArrowUp') { e.preventDefault(); setI((x) => Math.max(x - 1, 0)); }
          if (e.key === 'Enter' && list[i]) onPick(list[i].id);
          if (e.key === 'Escape') onClose();
        }} />
      <div style={{ marginTop: 6 }}>
        {list.map((p, k) => (
          <button key={p.id || 'none'} className={s.popItem} data-on={k === i || undefined} onMouseEnter={() => setI(k)} onClick={() => onPick(p.id)}>
            <span className="dot" style={{ background: p.color || 'var(--c-text-faint)' }} /><span>{p.icon}</span><span>{p.name}</span>
          </button>
        ))}
        {!list.length ? <div className="empty">No project matches.</div> : null}
      </div>
    </div>
  );
}
